import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaFacebookF, FaInstagram, FaWhatsapp, FaYoutube } from 'react-icons/fa';
import { FiArrowRight, FiCompass, FiHeart, FiMail, FiPhoneCall, FiShield } from 'react-icons/fi';
import YoutubeIntro from '../blocks/YoutubeIntro';
import TestimonialsBlock from '../blocks/TestimonialsBlock';
import Logo from '../../assets/Logo.svg'
import MetaData from '../../utils/MetaData'

function AboutUs() {
  const { t } = useTranslation();
  const navPage = useNavigate();
  const aboutUs = t('aboutUs', { returnObjects: true });
  const contact = t('contact', { returnObjects: true });
  const featureIcons = [FiCompass, FiShield, FiHeart, FiShield];
  const aboutFeatures = aboutUs?.features || { title: '', subtitle: '', items: [] };
  const aboutTestimonials = aboutUs?.testimonials || { title: '', subtitle: '' };

  return (
    <>
      <MetaData
        title='About Be Alright Krabi | Local Travel Experts in Krabi'
        description='Learn about Be Alright Krabi, your local travel partner for curated Krabi tours, premium experiences, and trusted on-ground support.'
        keywords='About Be Alright Krabi, Krabi travel experts, Krabi local tour company, Thailand travel specialists'
        canonicalUrl={`${window.location.origin}/about`}
        ogTitle='About Be Alright Krabi | Local Travel Experts in Krabi'
        ogDescription='Meet the local team behind premium Krabi experiences and trusted travel support.'
        ogType='website'
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: 'About Be Alright Krabi',
          url: `${window.location.origin}/about`
        }}
        structuredDataId='about-jsonld'
      />
      <div className='mx-auto my-5 w-[94%] max-w-6xl'>
        <h2 className='mb-4 text-center text-3xl font-black text-[#1739bd] sm:text-4xl'>{aboutUs.title}</h2>

        <div className='grid items-center gap-6 lg:grid-cols-[0.9fr_1.1fr]'>
          <img className='mx-auto w-full max-w-sm object-contain' src={Logo} alt='About Us' />
          <div>
            <p className='text-sm leading-7 text-slate-600 sm:text-base'>{aboutUs.content[0]}</p>
            <p className='mt-4 text-sm leading-7 text-slate-600 sm:text-base'>{aboutUs.content[1]}</p>
            <button
              className='group mt-3 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#092FB6] to-[#636DE5] px-6 py-3 text-sm font-semibold text-white shadow-[0_12px_30px_rgba(9,47,182,0.35)] transition hover:brightness-110 sm:text-base'
              onClick={() => navPage('/packages')}
            >
              {aboutUs.cta}
              <FiArrowRight className='transition-transform duration-300 group-hover:translate-x-1' />
            </button>
          </div>
        </div>

        <div className='mt-8 rounded-[1.75rem] border border-slate-200 bg-white p-4 shadow-[0_18px_50px_rgba(15,23,42,0.08)] sm:p-6'>
          <YoutubeIntro />
        </div>

        <section className='mt-8 rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-[0_18px_50px_rgba(15,23,42,0.08)] sm:p-8' aria-labelledby='about-features-heading'>
          <h3 id='about-features-heading' className='text-2xl font-black text-slate-950'>{aboutFeatures.title}</h3>
          <p className='mt-2 text-sm font-medium text-slate-500'>{aboutFeatures.subtitle}</p>

          <div className='mt-6 grid gap-4 md:grid-cols-2'>
            {aboutFeatures.items.map((feature, index) => {
              const Icon = featureIcons[index] || FiShield;
              return (
                <article key={feature.title} className='rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-5 shadow-[0_8px_24px_rgba(15,23,42,0.06)]'>
                  <span className='mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-50 text-xl text-[#1739bd]'>
                    <Icon />
                  </span>
                  <h4 className='text-base font-extrabold text-slate-900'>{feature.title}</h4>
                  <p className='mt-2 text-sm leading-7 text-slate-600'>{feature.desc}</p>
                </article>
              );
            })}
          </div>
        </section>

        <TestimonialsBlock title={aboutTestimonials.title} subtitle={aboutTestimonials.subtitle} />

        <div className='mt-8 rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-[0_18px_50px_rgba(15,23,42,0.08)] sm:p-8'>
          <h3 className='text-2xl font-black text-slate-950'>{contact.title}</h3>
          <p className='mt-2 text-sm font-semibold text-slate-500'>{contact.phone}</p>

          <div className='mt-4 grid gap-4 md:grid-cols-2'>
            <a className='group flex items-center gap-3 rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 px-4 py-3.5 text-sm font-semibold text-slate-700 shadow-[0_8px_24px_rgba(15,23,42,0.06)] transition hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-[0_12px_30px_rgba(9,47,182,0.14)] hover:text-[#1739bd]' href='tel:+66658106892'>
              <span className='inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white text-[#1739bd] shadow-sm ring-1 ring-slate-200 transition group-hover:ring-indigo-200'>
                <FiPhoneCall className='text-lg' />
              </span>
              (+66) 658106892 Be Alright Krabi
            </a>
            <a className='group flex items-center gap-3 rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 px-4 py-3.5 text-sm font-semibold text-slate-700 shadow-[0_8px_24px_rgba(15,23,42,0.06)] transition hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-[0_12px_30px_rgba(9,47,182,0.14)] hover:text-[#1739bd]' href='tel:+66817191748'>
              <span className='inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white text-[#1739bd] shadow-sm ring-1 ring-slate-200 transition group-hover:ring-indigo-200'>
                <FiPhoneCall className='text-lg' />
              </span>
              (+66) 817191748 {contact.nameOne}
            </a>
            <a className='group flex items-center gap-3 rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 px-4 py-3.5 text-sm font-semibold text-slate-700 shadow-[0_8px_24px_rgba(15,23,42,0.06)] transition hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-[0_12px_30px_rgba(9,47,182,0.14)] hover:text-[#1739bd]' href='tel:+66991646476'>
              <span className='inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white text-[#1739bd] shadow-sm ring-1 ring-slate-200 transition group-hover:ring-indigo-200'>
                <FiPhoneCall className='text-lg' />
              </span>
              (+66) 991646476 {contact.nameTwo}
            </a>
            <a className='group flex items-center gap-3 rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 px-4 py-3.5 text-sm font-semibold text-slate-700 shadow-[0_8px_24px_rgba(15,23,42,0.06)] transition hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-[0_12px_30px_rgba(9,47,182,0.14)] hover:text-[#1739bd]' href='mailto:bealrightkrabi@gmail.com'>
              <span className='inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white text-[#1739bd] shadow-sm ring-1 ring-slate-200 transition group-hover:ring-indigo-200'>
                <FiMail className='text-lg' />
              </span>
              bealrightkrabi@gmail.com
            </a>
          </div>

          <div className='mt-6 flex flex-wrap justify-center gap-3'>
            <a href='https://wa.me/+66658106892' target='_blank' rel='noopener noreferrer' className='inline-flex h-12 w-12 items-center justify-center rounded-full border border-slate-200 bg-white text-[#25D366] shadow-sm transition hover:-translate-y-0.5 hover:shadow-md' aria-label='WhatsApp'>
              <FaWhatsapp className='text-[1.18rem]' />
            </a>
            <a href='https://www.youtube.com/channel/UCLQdXEqwz5Y74XmL91z368Q' target='_blank' rel='noopener noreferrer' className='inline-flex h-12 w-12 items-center justify-center rounded-full border border-slate-200 bg-white text-[#FF0000] shadow-sm transition hover:-translate-y-0.5 hover:shadow-md' aria-label='YouTube'>
              <FaYoutube className='text-[1.18rem]' />
            </a>
            <a href='https://www.instagram.com/krabibealrighttour' target='_blank' rel='noopener noreferrer' className='inline-flex h-12 w-12 items-center justify-center rounded-full border border-slate-200 bg-white text-[#E1306C] shadow-sm transition hover:-translate-y-0.5 hover:shadow-md' aria-label='Instagram'>
              <FaInstagram className='text-[1.18rem]' />
            </a>
            <a href='https://www.facebook.com/p/Krabi-Be-Alright-Tour-61563971601520' target='_blank' rel='noopener noreferrer' className='inline-flex h-12 w-12 items-center justify-center rounded-full border border-slate-200 bg-white text-[#1877F2] shadow-sm transition hover:-translate-y-0.5 hover:shadow-md' aria-label='Facebook'>
              <FaFacebookF className='text-[1.12rem]' />
            </a>
          </div>

          <iframe
            className='mt-6 h-[300px] w-full rounded-[1.25rem] border-0 sm:h-[340px]'
            src='https://maps.google.com/maps?q=Be%20Alright%20Tour%20Krabi&t=k&z=17&output=embed'
            title='Google map'
            loading='lazy'
            referrerPolicy='no-referrer-when-downgrade'
            allowFullScreen
          />
        </div>
      </div>
    </>
  );
}

export default AboutUs;
